import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { VISUAL_THEME } from '../../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH } from '../../data/gameConstants';
import { ObstacleRect } from '../../systems/ZoneCoreSystems';
import { UIStyle } from '../../ui/UIStyle';

export const ZONE4_POSITIONS = {
  SPAWN: { x: 160, y: 560 },
  KERIS: { x: 340, y: 470 },
  FORK: { x: 520, y: 450 },
  /** Titik interaksi pilih jalur — di persimpangan bawah (terpisah dari papan). */
  FORK_INTERACT: { x: 520, y: 502 },
  SIGN: { x: 520, y: 400 },
  /** Titik interaksi papan — di jalur bawah (pemain tidak bisa naik ke sprite papan). */
  SIGN_INTERACT: { x: 520, y: 472 },
  GUIDE: { x: 605, y: 525 },
  RIVER_ROUTE: { x: 420, y: 580 },
  HILL_ROUTE: { x: 780, y: 420 },
  HILL_PATH_MID: { x: 900, y: 385 },
  HILL_EXIT: { x: 1050, y: 400 },
  SAFE_EXIT: { x: 1050, y: 400 },
  EXIT_BLOCK: { x: 930, y: 450, width: 70, height: 130 },
} as const;

/** Pusat obstacle yang dibuka setelah memilih jalur — koridor ke titik kumpul aman */
export const ZONE4_EXIT_CLEAR_BLOCKS = [
  ZONE4_POSITIONS.EXIT_BLOCK,
  { x: 820, y: 430 },
  { x: 560, y: 430 },
  { x: 1085, y: 405 },
  { x: 950, y: 340 },
] as const;

export class Zone4Map {
  private scene: Phaser.Scene;
  private exitLabel?: Phaser.GameObjects.Text;
  private hillRouteLabel?: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  build(): ObstacleRect[] {
    this.drawBackground();
    this.drawPaths();
    this.drawDecorations();
    return this.buildObstacles();
  }

  openExitPath(): void {
    this.exitLabel?.setColor('#52b788');
    this.exitLabel?.setText('→ Titik Kumpul Aman\n(Terbuka)');
    this.hillRouteLabel?.setColor('#7dffb8');
    this.hillRouteLabel?.setText('Jalur Bukit Terbuka\n(Terbuka)');
  }

  private drawBackground(): void {
    this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_ZONE4)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.zone4, 0.08,
    ).setDepth(0.5);

    const hills = this.scene.add.graphics().setDepth(1);
    hills.fillStyle(0x52b788, 0.4);
    hills.fillTriangle(700, 350, 1100, 200, 1200, 400);
    hills.fillStyle(0x6ee7a8, 0.3);
    hills.fillTriangle(50, 450, 250, 300, 350, 480);
    hills.fillStyle(0x6b4f36, 0.18);
    hills.fillTriangle(640, 470, 780, 330, 860, 470);
    hills.fillTriangle(960, 470, 1100, 250, 1220, 470);
    hills.fillStyle(0xffffff, 0.08);
    hills.fillRect(0, 350, GAME_WIDTH, 38);
  }

  private drawPaths(): void {
    const g = this.scene.add.graphics().setDepth(2);
    g.fillStyle(0xa1887f, VISUAL_THEME.pathAlpha);
    g.fillEllipse(300, 530, 280, 50);
    g.fillEllipse(520, 480, 120, 40);

    this.drawRiver();

    this.scene.add.image(ZONE4_POSITIONS.HILL_ROUTE.x, ZONE4_POSITIONS.HILL_ROUTE.y,
      TEXTURE_KEYS.PATH_HILL,
    ).setScale(1.3).setDepth(3);
    this.scene.add.image(ZONE4_POSITIONS.HILL_PATH_MID.x, ZONE4_POSITIONS.HILL_PATH_MID.y,
      TEXTURE_KEYS.PATH_HILL,
    ).setScale(1.05, 0.82).setDepth(3.1).setAngle(-12).setAlpha(0.92);
    this.scene.add.image(1015, 372, TEXTURE_KEYS.PATH_HILL)
      .setScale(0.88, 0.68).setDepth(3.2).setAngle(-10).setAlpha(0.88);

    this.hillRouteLabel = this.scene.add.text(
      ZONE4_POSITIONS.HILL_ROUTE.x,
      ZONE4_POSITIONS.HILL_ROUTE.y - 55,
      'Jalur Bukit\n(Terkunci)',
      {
        ...UIStyle.mapLabel(14, '#ff6b6b'),
        align: 'center',
        lineSpacing: 4,
      },
    ).setOrigin(0.5, 1).setDepth(5);
    this.scene.add.text(650, 410, 'Lereng curam', {
      ...UIStyle.mapLabel(13, '#fff3d6'),
    }).setOrigin(0.5).setDepth(5);
    this.scene.add.text(1115, 372, 'Tebing curam', {
      ...UIStyle.mapLabel(13, '#fff3d6'),
    }).setOrigin(0.5).setDepth(5);

    this.scene.add.image(880, 350, TEXTURE_KEYS.SAFE_GATHERING).setScale(0.45).setDepth(4).setAlpha(0.75);
  }

  /** Sungai organik — tepi lumpur, aliran berlapis, riak halus (bukan kotak sprite). */
  private drawRiver(): void {
    const g = this.scene.add.graphics().setDepth(2.4);

    // Tepi sungai / lumpur basah
    g.fillStyle(0x5d4e37, 0.55);
    g.fillEllipse(295, 632, 95, 30);
    g.fillEllipse(355, 625, 130, 34);
    g.fillEllipse(445, 628, 120, 30);
    g.fillEllipse(520, 622, 95, 28);
    g.fillEllipse(565, 615, 70, 24);

    g.fillStyle(0x6b8f5e, 0.35);
    g.fillEllipse(288, 618, 55, 16);
    g.fillEllipse(572, 608, 48, 14);

    // Kedalaman air — lapisan gelap ke terang
    g.fillStyle(0x0d47a1, 0.72);
    g.fillEllipse(380, 622, 260, 52);
    g.fillEllipse(410, 618, 200, 40);

    g.fillStyle(0x1565c0, 0.62);
    g.fillEllipse(390, 616, 220, 36);
    g.fillEllipse(430, 620, 160, 28);

    g.fillStyle(0x29b6f6, 0.48);
    g.fillEllipse(400, 612, 190, 26);
    g.fillEllipse(460, 615, 120, 20);

    g.fillStyle(0x81d4fa, 0.28);
    g.fillEllipse(340, 608, 55, 12);
    g.fillEllipse(500, 610, 65, 11);

    // Riak permukaan & aliran
    g.fillStyle(0xffffff, 0.16);
    g.fillEllipse(330, 614, 38, 5);
    g.fillEllipse(375, 618, 48, 4);
    g.fillEllipse(420, 614, 42, 5);
    g.fillEllipse(465, 617, 36, 4);
    g.fillEllipse(510, 620, 32, 4);

    g.lineStyle(1.2, 0xffffff, 0.1);
    for (let i = 0; i < 7; i++) {
      const x = 305 + i * 36;
      const y = 610 + (i % 2) * 5;
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(x + 26, y + 3);
      g.strokePath();
    }

    // Zona bahaya tepi — tint halus, bukan segitiga tajam
    g.fillStyle(0xc1121f, 0.1);
    g.fillEllipse(302, 604, 44, 16);
    g.fillEllipse(558, 606, 40, 15);

    // Jalur tepi sungai (tanah basah) di atas berm
    const path = this.scene.add.graphics().setDepth(2.6);
    path.fillStyle(0x8d6e63, 0.78);
    path.fillEllipse(355, 592, 175, 20);
    path.fillStyle(0xa1887f, 0.55);
    path.fillEllipse(350, 590, 160, 14);

    this.scene.add.text(ZONE4_POSITIONS.RIVER_ROUTE.x, ZONE4_POSITIONS.RIVER_ROUTE.y - 48, 'Jalur Tepi Sungai', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);
    this.scene.add.text(ZONE4_POSITIONS.FORK_INTERACT.x, ZONE4_POSITIONS.FORK_INTERACT.y - 42, 'Pilih Jalur Evakuasi', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);

    // Ilalang / rumput di tepi
    g.lineStyle(2, 0x558b2f, 0.5);
    const reeds: [number, number][] = [[278, 600], [290, 605], [575, 598], [588, 602]];
    reeds.forEach(([x, y]) => {
      g.beginPath();
      g.moveTo(x, y + 14);
      g.lineTo(x - 3, y);
      g.lineTo(x + 1, y + 14);
      g.closePath();
      g.fillPath();
    });
  }

  private drawDecorations(): void {
    const sign = ZONE4_POSITIONS.SIGN;
    this.scene.add.image(sign.x, sign.y, TEXTURE_KEYS.SIGN_BOARD).setScale(1.1).setDepth(4);
    this.scene.add.text(sign.x, sign.y - 58, 'Persimpangan', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);

    this.scene.add.image(ZONE4_POSITIONS.HILL_EXIT.x, ZONE4_POSITIONS.HILL_EXIT.y - 30,
      TEXTURE_KEYS.SAFE_GATHERING,
    ).setScale(0.55).setDepth(3).setAlpha(0.7);

    this.exitLabel = this.scene.add.text(
      ZONE4_POSITIONS.SAFE_EXIT.x,
      ZONE4_POSITIONS.SAFE_EXIT.y - 72,
      'Titik Kumpul Aman\n(Terkunci)',
      {
        ...UIStyle.mapLabel(14, '#ff6b6b'),
        align: 'center',
        lineSpacing: 4,
      },
    ).setOrigin(0.5, 1).setDepth(5);

    this.scene.add.image(250, 500, TEXTURE_KEYS.TREE_LARGE).setScale(0.5).setDepth(3);
    this.scene.add.image(650, 380, TEXTURE_KEYS.TREE_LARGE).setScale(0.45).setFlipX(true).setDepth(3);
    this.scene.add.image(950, 360, TEXTURE_KEYS.TREE_LARGE).setScale(0.5).setDepth(3);
    this.scene.add.image(320, 500, TEXTURE_KEYS.KERIS_3).setScale(0.78).setDepth(4).setAngle(-10);
    this.scene.add.text(320, 438, 'Pertimbangkan jalur aman', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);
  }

  private buildObstacles(): ObstacleRect[] {
    return [
      // Langit + bukit — blok seluruh area atas (sama seperti Zona 1–3)
      { x: GAME_WIDTH / 2, y: 165, width: GAME_WIDTH, height: 330 },
      // Lantai koridor — cegah turun ke bawah peta
      { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 20, width: GAME_WIDTH, height: 40 },
      // Dinding kiri/kanan
      { x: 30, y: GAME_HEIGHT / 2, width: 60, height: GAME_HEIGHT },
      { x: GAME_WIDTH - 20, y: GAME_HEIGHT / 2, width: 40, height: GAME_HEIGHT },
      // Semak/bukit latar — blok naik ke lereng
      { x: 170, y: 405, width: 320, height: 110 },
      { x: 680, y: 398, width: 460, height: 110 },
      { x: 1085, y: 405, width: 290, height: 110 },
      // Pohon/dekor di tepi jalur
      { x: 250, y: 480, width: 50, height: 70 },
      { x: 650, y: 360, width: 45, height: 65 },
      { x: 950, y: 340, width: 50, height: 70 },
      // Penghalang lereng — tutup celah naik ke bukit
      { x: 820, y: 430, width: 280, height: 90 },
      { x: 610, y: 418, width: 90, height: 55 },
      { x: 395, y: 395, width: 130, height: 85 },
      // Persimpangan & keris — blok objek dekoratif
      { x: ZONE4_POSITIONS.SIGN.x, y: ZONE4_POSITIONS.SIGN.y + 10, width: 35, height: 40 },
      { x: ZONE4_POSITIONS.GUIDE.x, y: ZONE4_POSITIONS.GUIDE.y - 10, width: 40, height: 50 },
      // Blok jalur ke titik kumpul — dibuka setelah memilih jalur
      {
        x: ZONE4_POSITIONS.EXIT_BLOCK.x,
        y: ZONE4_POSITIONS.EXIT_BLOCK.y,
        width: ZONE4_POSITIONS.EXIT_BLOCK.width,
        height: ZONE4_POSITIONS.EXIT_BLOCK.height,
      },
      // Tepi sungai — cegah masuk ke air
      { x: 380, y: 630, width: 180, height: 30 },
    ];
  }
}
